import { useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { MotivationOverlay } from './MotivationOverlay';
import { api, TICKET_BEREICH_GRUPPEN, TICKET_TYPEN, type Blechbedarf, type Lieferant } from '../lib/api';
import { WerkstoffArchiv } from './WerkstoffArchiv';
import { useEntwurf, useEntwurfKey } from '../hooks/useEntwurf';
import { EntwurfBanner } from './EntwurfBanner';
import { EinkaufNeuProvider, ZaehlerBadge, neuAnzahl, useEinkaufNeu } from './EinkaufNeu';
import { TicketNeuProvider, TicketZaehlerBadge, useTicketNeu } from './TicketNeu';
import { AutoUpdate } from './AutoUpdate';

const navItems = [
  { to: '/dashboard',    label: 'Dashboard' },
  { to: '/drueckteile',  label: 'Drückteile' },
  { to: '/laufraeder',   label: 'Laufräder' },
  { to: '/baugruppen',   label: 'Baugruppen' },
  { to: '/montage',      label: 'Montage' },
  { to: '/laser_kant',   label: 'Laser / Kant' },
  { to: '/werkstatt',    label: 'Werkstatt-Modus' },
  { to: '/qs',           label: 'QS-Meldungen' },
  { to: '/wareneingang', label: 'Wareneingang' },
  { to: '/kundeninfo',   label: 'Kunden informieren' },
  { to: '/kunden',       label: 'Kunden' },
  { to: '/kapazitaeten', label: 'Kapazitäten' },
  { to: '/statistik',    label: 'Statistik' },
  { to: '/sonderteile',  label: 'Sonderteile-Anfragen' },
];

// Einkauf-Modul (E1): Blechbedarf + Lieferantenstamm; wächst mit E2-E4
// (Anfragen/Preisspiegel, Einkauf-Dashboard, Auswertungen).
const einkaufItems = [
  { to: '/einkauf',       label: 'Einkauf-Dashboard' },
  { to: '/banf', label: '📋 Bestellanforderungen (BANF)' },
  { to: '/blechbedarf',   label: 'Blechbedarf' },
  { to: '/profile-rohre', label: 'Profile & Rohre' },
  { to: '/einkauf-anfragen', label: 'Weitere Anfragen (Loxpal u.a.)' },
  { to: '/gas',           label: 'Gasbestellung' },
  { to: '/lieferanten',   label: 'Lieferanten' },
  { to: '/artikelstamm', label: '📚 Artikelstamm' },
];

// Tickets ist ein eigenständiges Modul (tickets.sickinger-portal.de) —
// nicht Teil des Einkaufs und nicht in der Haupt-Navigation.
const ticketsItems = [
  { to: '/meine-tickets', label: '📋 Meine Tickets' },
  { to: '/tickets', label: '🎫 Tickets' },
];

const adminItems = [
  { to: '/admin/backups', label: 'Backup & Recovery' },
  { to: '/einstellungen', label: 'Einstellungen' },
];

// Noch nicht implementierte Module (Phase 3+)
const phaseItems = [
  { label: 'Lackiererei',  phase: 3 },
];

// Modul-Modus: über blechbedarf.* bzw. einkauf.* wirkt die App wie ein
// eigenständiges Modul (gleicher Worker, gleiche DB, eigene Optik).
// blechbedarf.* zeigt bewusst NUR Blechbedarf (bestehender Test-Einstieg).
const isBlechbedarfHost = typeof window !== 'undefined'
  && window.location.hostname.startsWith('blechbedarf.');
const isEinkaufHost = typeof window !== 'undefined'
  && window.location.hostname.startsWith('einkauf.');
const isTicketsHost = typeof window !== 'undefined'
  && window.location.hostname.startsWith('tickets.');
const isModulHost = isBlechbedarfHost || isEinkaufHost || isTicketsHost;

// Browser-Tab-Titel passend zum Modul (index.html trägt "Sickinger Terminliste")
if (typeof document !== 'undefined') {
  if (isBlechbedarfHost) document.title = 'Sickinger Blechbedarf';
  else if (isEinkaufHost) document.title = 'Sickinger Einkauf';
  else if (isTicketsHost) document.title = 'Sickinger Tickets';
}

// Globale Suche (Sidebar): sucht ab dem ersten Zeichen (250 ms entprellt)
// in Blechbedarf (Werkstoff/Format/Referenz/Lieferant/Bestell-Nr) und im
// Lieferantenstamm; Treffer-Klick springt zur passenden Seite.
function GlobalSuche() {
  const [term, setTerm] = useState('');
  const [bedarfe, setBedarfe] = useState<Blechbedarf[]>([]);
  const [liefTreffer, setLiefTreffer] = useState<Lieferant[]>([]);
  const [offenListe, setOffenListe] = useState(false);
  const alleLieferanten = useRef<Lieferant[] | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = term.trim();
    if (!t) { setBedarfe([]); setLiefTreffer([]); setOffenListe(false); return; }
    const timer = setTimeout(async () => {
      try {
        if (!alleLieferanten.current) {
          alleLieferanten.current = (await api.lieferanten.list()).data;
        }
        const r = await api.blechbedarf.list({ status: 'alle', q: t, limit: 6 });
        setBedarfe(r.data);
        setLiefTreffer(alleLieferanten.current.filter(l => l.name.toLowerCase().includes(t.toLowerCase())).slice(0, 4));
        setOffenListe(true);
      } catch { /* Suche still scheitern lassen */ }
    }, 250);
    return () => clearTimeout(timer);
  }, [term]);

  const geheZu = (ziel: string) => { setOffenListe(false); setTerm(''); navigate(ziel); };
  const zurListe = () => geheZu(`/blechbedarf?status=alle&q=${encodeURIComponent(term.trim())}`);

  return (
    <div className="relative mt-3">
      <input
        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-brand"
        placeholder="🔍 Suche …"
        value={term}
        onChange={e => setTerm(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && term.trim()) zurListe(); if (e.key === 'Escape') { setTerm(''); setOffenListe(false); } }}
        onFocus={() => { if (term.trim() && (bedarfe.length || liefTreffer.length)) setOffenListe(true); }}
      />
      {offenListe && (bedarfe.length > 0 || liefTreffer.length > 0) && (
        <div className="absolute left-0 right-[-140px] top-full mt-1 bg-white text-slate-900 rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden text-sm">
          {bedarfe.length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Blechbedarf</div>}
          {bedarfe.map(b => (
            <button key={b.id} onClick={zurListe}
                    className="block w-full text-left px-3 py-1.5 hover:bg-slate-100">
              <span className="font-medium">{b.werkstoff ?? '—'}</span> {b.format ?? ''}{b.dicke ? ` · ${b.dicke}mm` : ''}
              <span className="text-slate-500"> {b.referenz ?? ''}</span>
              <span className="ml-1 text-xs text-slate-400">{b.fertig ? 'fertig' : b.bestellt ? 'bestellt' : 'offen'}</span>
            </button>
          ))}
          {liefTreffer.length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Lieferanten</div>}
          {liefTreffer.map(l => (
            <button key={l.id} onClick={() => geheZu('/lieferanten')}
                    className="block w-full text-left px-3 py-1.5 hover:bg-slate-100">
              {l.name}{l.email ? <span className="text-slate-400 text-xs ml-1">{l.email}</span> : null}
            </button>
          ))}
          <button onClick={zurListe} className="block w-full text-left px-3 py-2 text-brand hover:bg-slate-50 border-t border-slate-100">
            Alle Treffer für „{term.trim()}" anzeigen →
          </button>
        </div>
      )}
    </div>
  );
}

// Zettel-Ersatz: Von JEDER Seite aus etwas beim Einkauf anfordern (z. B.
// Lackierer braucht Muffen). Landet als Aufgabe im Einkauf-Dashboard.
function EinkaufMelden() {
  const [offen, setOffen] = useState(false);
  const [titel, setTitel] = useState('');
  const [wer, setWer] = useState('');
  const [ansprechpartner, setAnsprechpartner] = useState('');
  const [typ, setTyp] = useState<'bestellung' | 'anfrage'>('bestellung');
  const [eilig, setEilig] = useState(false);
  const [bild, setBild] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  // Entwurf (Autosave): Foto wird nicht gesichert (Datei), der Rest schon
  const stand = { titel, wer, ansprechpartner, typ, eilig };
  const entWert = useMemo(() => (offen ? stand : null), [offen, titel, wer, ansprechpartner, typ, eilig]); // eslint-disable-line react-hooks/exhaustive-deps
  const ent = useEntwurf(useEntwurfKey('einkauf-melden'), entWert,
    v => !!(v.titel.trim() || v.wer.trim() || v.ansprechpartner.trim() || v.eilig));

  const reset = () => { setTitel(''); setWer(''); setAnsprechpartner(''); setTyp('bestellung'); setEilig(false); setBild(null); };

  const senden = async () => {
    if (!titel.trim()) return;
    setBusy(true);
    try {
      const r = await api.einkaufAufgaben.create({
        titel, angefordert_von: wer || undefined, prio: eilig ? 'eilig' : 'normal',
        typ, ansprechpartner: ansprechpartner || undefined,
      });
      if (bild && r.data?.id) {
        try { await api.einkaufAufgaben.bildHochladen(r.data.id, bild); } catch { /* Meldung ist trotzdem angekommen */ }
      }
      setOk(true); reset(); ent.nachSpeichern();
      setTimeout(() => { setOk(false); setOffen(false); }, 1500);
    } catch { alert('Senden fehlgeschlagen — bitte nochmal versuchen.'); }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => { setOffen(true); ent.beimOeffnen(stand); }}
              className="w-full mt-3 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg px-3 py-2 text-sm font-semibold">
        🛒 Bedarf an Einkauf melden
        {ent.entwurf && !offen && <span className="ml-2 rounded-full bg-white/70 text-amber-950 text-[11px] font-semibold px-2 py-0.5" title="Nicht gesendeter Entwurf — nur in diesem Browser">📝 Entwurf</span>}
      </button>
      {offen && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-start justify-center p-4 pt-24" onClick={() => setOffen(false)}>
          <div className="bg-white text-slate-900 rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold">🛒 Bedarf an Einkauf melden</h2>
              <button onClick={() => setOffen(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>
            {ok ? (
              <div className="p-8 text-center text-emerald-700 font-semibold">✓ Beim Einkauf gemeldet<div className="mt-1 text-xs font-normal text-slate-500">Zu sehen unter Anfragen › Gemeldet</div></div>
            ) : (
              <div className="p-5 space-y-3">
                {ent.wartet && ent.entwurf && (
                  <EntwurfBanner at={ent.entwurf.at} zusammenfassung={ent.entwurf.data.titel.trim() || undefined}
                    onWiederherstellen={() => { const d = ent.wiederherstellen(); if (d) { setTitel(d.titel); setWer(d.wer); setAnsprechpartner(d.ansprechpartner); setTyp(d.typ); setEilig(d.eilig); } }}
                    onVerwerfen={ent.verwerfen} />
                )}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Was wird gebraucht?</label>
                  <input autoFocus className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                         placeholder="z. B. 10x Muffe 1/2 Zoll · Lack RAL 5010 · Trennscheiben"
                         value={titel} onChange={e => setTitel(e.target.value)}
                         onKeyDown={e => { if (e.key === 'Enter') void senden(); }} />
                </div>
                <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5 w-fit"
                     title="Bestellung = echter Bedarf · Anfrage = reine Preisermittlung">
                  {([['bestellung', '🔵 Bestellung'], ['anfrage', '🟣 Anfrage']] as const).map(([v, label]) => (
                    <button key={v} type="button" onClick={() => setTyp(v)}
                            className={`px-3 py-1.5 rounded text-sm font-medium ${typ === v ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">Wer fragt an?</label>
                    <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                           placeholder="Name / Kürzel" value={wer} onChange={e => setWer(e.target.value)} />
                  </div>
                  <label className="flex items-end gap-1.5 text-sm pb-2">
                    <input type="checkbox" checked={eilig} onChange={e => setEilig(e.target.checked)} /> eilig
                  </label>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Ansprechpartner beim Lieferanten (optional)</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                         placeholder="Name / Firma" value={ansprechpartner} onChange={e => setAnsprechpartner(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Foto (optional)</label>
                  <input type="file" accept="image/*" className="w-full text-sm"
                         onChange={e => setBild(e.target.files?.[0] ?? null)} />
                </div>
                <button onClick={() => void senden()} disabled={busy || !titel.trim()}
                        className="w-full bg-brand text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
                  {busy ? 'Sendet …' : 'Absenden'}
                </button>
                <div className="text-xs text-slate-400">Landet sofort in der Aufgabenliste des Einkaufs (Jasmin).</div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Ticket-Master: jeder kann einen Fehler/Wunsch digital melden (D6)
function TicketMelden() {
  const [offen, setOffen] = useState(false);
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [bereich, setBereich] = useState('');
  const [prio, setPrio] = useState('normal');
  const [typ, setTyp] = useState<'fehler' | 'wunsch' | 'frage'>('fehler');
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  // Anhänge direkt beim Melden (Ticket T-12): Datei wählen, Strg+V oder hineinziehen
  const [anhaenge, setAnhaenge] = useState<File[]>([]);
  const anhangDazu = (liste: File[]) => {
    const neu = liste.filter(f => /^image\//.test(f.type) || f.type === 'application/pdf' || /\.(png|jpe?g|gif|webp|pdf)$/i.test(f.name));
    if (neu.length < liste.length) alert('Nur Bilder (PNG/JPG/GIF/WEBP) oder PDF.');
    const zuGross = neu.filter(f => f.size > 15 * 1024 * 1024);
    if (zuGross.length) alert('Datei zu groß (max. 15 MB): ' + zuGross.map(f => f.name).join(', '));
    const passend = neu.filter(f => f.size <= 15 * 1024 * 1024);
    if (passend.length) setAnhaenge(a => [...a, ...passend].slice(0, 10));
  };
  const beimEinfuegen = (e: ClipboardEvent<HTMLDivElement>) => {
    const dateien: File[] = [];
    for (const it of Array.from(e.clipboardData?.items ?? [])) {
      if (it.kind !== 'file') continue;
      const f = it.getAsFile();
      if (!f) continue;
      const endung = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
      const stempel = new Date().toISOString().slice(0, 19).replace(/\D/g, '');
      dateien.push(f.name && f.name !== 'image.png' ? f : new File([f], `screenshot-${stempel}-${dateien.length + 1}.${endung}`, { type: f.type }));
    }
    if (dateien.length) { e.preventDefault(); anhangDazu(dateien); }
  };
  // Entwurf (Autosave)
  const stand = { titel, beschreibung, bereich, prio, typ };
  const entWert = useMemo(() => (offen ? stand : null), [offen, titel, beschreibung, bereich, prio, typ]); // eslint-disable-line react-hooks/exhaustive-deps
  const ent = useEntwurf(useEntwurfKey('ticket-melden'), entWert,
    v => !!(v.titel.trim() || v.beschreibung.trim() || v.bereich || v.prio !== 'normal'));

  const senden = async () => {
    if (!titel.trim()) return;
    setBusy(true);
    try {
      const neu = await api.tickets.create({ titel, beschreibung: beschreibung || undefined, bereich: bereich || undefined, prio, typ });
      const fehlgeschlagen: string[] = [];
      for (const f of anhaenge) {
        try { await api.tickets.dateiHochladen(neu.data.id, f); } catch { fehlgeschlagen.push(f.name); }
      }
      if (fehlgeschlagen.length) alert(`Ticket #T-${neu.data.id} wurde angelegt, aber diese Anhänge konnten nicht hochgeladen werden: ${fehlgeschlagen.join(', ')}. Bitte im Ticket unter „Verlauf & Anhänge" nochmal hochladen.`);
      setOk(true); setTitel(''); setBeschreibung(''); setBereich(''); setPrio('normal'); setTyp('fehler'); setAnhaenge([]); ent.nachSpeichern();
      setTimeout(() => { setOk(false); setOffen(false); }, 1500);
    } catch { alert('Senden fehlgeschlagen — bitte nochmal versuchen.'); }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => { setOffen(true); ent.beimOeffnen(stand); }}
              className="w-full mt-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-3 py-2 text-sm font-semibold">
        🎫 Problem / Wunsch melden
        {ent.entwurf && !offen && <span className="ml-2 rounded-full bg-amber-300 text-amber-950 text-[11px] font-semibold px-2 py-0.5" title="Nicht gesendeter Entwurf — nur in diesem Browser">📝 Entwurf</span>}
      </button>
      {offen && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-start justify-center p-4 pt-24" onClick={() => setOffen(false)}>
          <div className="bg-white text-slate-900 rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}
               onPaste={beimEinfuegen}
               onDragOver={e => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
               onDrop={e => { if (e.dataTransfer.files.length) { e.preventDefault(); anhangDazu(Array.from(e.dataTransfer.files)); } }}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold">🎫 Ticket melden</h2>
              <button onClick={() => setOffen(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>
            {ok ? (
              <div className="p-8 text-center text-emerald-700 font-semibold">✓ Ticket gemeldet</div>
            ) : (
              <div className="p-5 space-y-3">
                {ent.wartet && ent.entwurf && (
                  <EntwurfBanner at={ent.entwurf.at} zusammenfassung={ent.entwurf.data.titel.trim() || undefined}
                    onWiederherstellen={() => { const d = ent.wiederherstellen(); if (d) { setTitel(d.titel); setBeschreibung(d.beschreibung); setBereich(d.bereich); setPrio(d.prio); setTyp(d.typ); } }}
                    onVerwerfen={ent.verwerfen} />
                )}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Art</label>
                  <div className="flex gap-1">
                    {(Object.keys(TICKET_TYPEN) as Array<'fehler' | 'wunsch' | 'frage'>).map(k => (
                      <button key={k} type="button" onClick={() => setTyp(k)}
                              className={`flex-1 rounded-lg px-2 py-1.5 text-sm font-medium border ${typ === k ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                        {TICKET_TYPEN[k].icon} {TICKET_TYPEN[k].label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Worum geht's? *</label>
                  <input autoFocus className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                         placeholder="z. B. Preis wird falsch übernommen"
                         value={titel} onChange={e => setTitel(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Beschreibung (optional)</label>
                  <textarea className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm min-h-[160px] resize-y" rows={8}
                            placeholder="Was ist passiert? Wo? Was hast du erwartet?"
                            value={beschreibung} onChange={e => setBeschreibung(e.target.value)} />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">Bereich</label>
                    <select className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                            value={bereich} onChange={e => setBereich(e.target.value)}>
                      <option value="">— wählen —</option>
                      {TICKET_BEREICH_GRUPPEN.map(g => (
                        <optgroup key={g.gruppe} label={g.gruppe}>
                          {g.bereiche.map(b => <option key={b} value={b}>{b}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Priorität</label>
                    <select className="border border-slate-300 rounded-lg px-2 py-2 text-sm" value={prio} onChange={e => setPrio(e.target.value)}>
                      <option value="normal">Normal</option><option value="wichtig">Wichtig</option><option value="dringend">Dringend</option>
                    </select>
                  </div>
                </div>
                <div data-testid="ticket-anhaenge">
                  <label className="block text-xs text-slate-500 mb-1">Bilder / Screenshots (optional)</label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 text-xs text-slate-500">
                    {anhaenge.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {anhaenge.map((f, i) => (
                          <div key={`${f.name}-${i}`} className="relative border border-slate-200 rounded-md overflow-hidden bg-slate-50" title={f.name}>
                            {f.type.startsWith('image/')
                              ? <img src={URL.createObjectURL(f)} alt={f.name} className="h-16 w-16 object-cover" />
                              : <div className="h-16 w-16 flex flex-col items-center justify-center p-1"><span className="text-xl">📄</span><span className="truncate max-w-[60px]">{f.name}</span></div>}
                            <button type="button" onClick={() => setAnhaenge(a => a.filter((_, j) => j !== i))}
                                    className="absolute top-0 right-0 bg-white/90 text-slate-500 hover:text-red-600 w-4 h-4 leading-none text-xs" title="Entfernen">×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="inline-block px-2.5 py-1 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 cursor-pointer">
                        📎 Bild / PDF wählen
                        <input type="file" accept="image/*,application/pdf" multiple className="hidden" data-testid="ticket-datei"
                               onChange={e => { anhangDazu(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
                      </label>
                      <span>oder Screenshot mit <b>Strg+V</b> einfügen / hineinziehen</span>
                    </div>
                  </div>
                </div>
                <button onClick={() => void senden()} disabled={busy || !titel.trim()}
                        className="w-full bg-brand text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
                  {busy ? (anhaenge.length ? 'Sendet + lädt Anhänge …' : 'Sendet …') : 'Ticket absenden'}
                </button>
                <div className="text-xs text-slate-400">Landet in der Ticket-Liste — wird in Ruhe bearbeitet.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function Layout() {
  // „Neue Bestellung“-Hinweise (Badge/NEU/Popup) gibt es nur im Einkauf-Modul
  return (
    <EinkaufNeuProvider aktiv={isEinkaufHost}>
      <TicketNeuProvider>
        <LayoutInhalt />
      </TicketNeuProvider>
    </EinkaufNeuProvider>
  );
}

function LayoutInhalt() {
  const { daten: neuDaten } = useEinkaufNeu();
  const { daten: ticketDaten } = useTicketNeu();
  const neuGesamt = neuDaten.aufgaben.length + new Set(neuDaten.banf.map(p => p.zettel_id)).size + neuDaten.bedarf.length + neuDaten.sonstiges.length
    + (isTicketsHost ? ticketDaten.tickets.length + (ticketDaten.melder?.length ?? 0) : 0);
  const items = isTicketsHost
    ? ticketsItems.filter(i => i.to !== '/tickets' || ticketDaten.bearbeiter !== false)   // nur Melder: kein Bearbeitungs-Menü
    : isBlechbedarfHost
      ? einkaufItems.filter(i => i.to === '/blechbedarf')
      : isEinkaufHost
        ? einkaufItems
        : navItems;
  // Mobil: Sidebar wird zum einblendbaren Drawer (Hamburger in der Topbar)
  const [navOffen, setNavOffen] = useState(false);
  return (
    <div className="flex h-screen flex-col md:flex-row">
      {/* Mobile Topbar */}
      <header className="md:hidden bg-slate-900 text-white flex items-center gap-3 px-4 py-2.5 shrink-0">
        <button onClick={() => setNavOffen(true)} aria-label="Menü öffnen"
                className="relative text-2xl leading-none px-1 -ml-1">☰
          {neuGesamt > 0 && <span data-testid="hamburger-badge" className="absolute -top-1 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{neuGesamt}</span>}
        </button>
        <a href="https://sickinger-portal.de/" target="_top" title="Zum Portal (Startseite)" className="flex items-center gap-3 min-w-0">
          <img src="/favicon.svg" alt="MS" className="w-7 h-7 rounded bg-white p-0.5" />
          <div className="font-semibold text-sm truncate">Manfred Sickinger GmbH</div>
        </a>
      </header>
      {navOffen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setNavOffen(false)} />
      )}
      <aside className={`${navOffen ? 'fixed inset-y-0 left-0 z-50 flex shadow-2xl' : 'hidden'} md:static md:flex md:z-auto w-72 max-w-[85vw] md:w-64 bg-slate-900 text-white flex-col`}
             onClick={e => { if ((e.target as HTMLElement).closest('a')) setNavOffen(false); }}>
        <div className="p-5 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <a href="https://sickinger-portal.de/" target="_top" title="Zum Portal (Startseite)" className="flex items-center gap-3 min-w-0 hover:opacity-80">
              <img src="/favicon.svg" alt="MS" className="w-10 h-10 rounded bg-white p-0.5" />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">Manfred Sickinger GmbH</div>
                <div className="text-xs text-slate-400">{isBlechbedarfHost ? 'Blechbedarf' : isEinkaufHost ? 'Einkauf' : isTicketsHost ? 'Tickets' : 'Terminliste 2026'}</div>
              </div>
            </a>
            <button onClick={() => setNavOffen(false)} aria-label="Menü schließen"
                    className="md:hidden ml-auto text-slate-400 hover:text-white text-xl leading-none">×</button>
          </div>
          <GlobalSuche />
          <EinkaufMelden />
          <TicketMelden />
        </div>

        <nav className="flex-1 p-3 space-y-1 text-sm overflow-y-auto">
          {items.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg ${
                  isActive ? 'bg-brand text-white' : 'text-slate-300 hover:bg-slate-800'
                }`
              }>
              <span>{item.label}</span>
              <ZaehlerBadge n={neuAnzahl(neuDaten, item.to)} />
              {item.to === '/tickets' && <TicketZaehlerBadge n={ticketDaten.tickets.length} />}
              {item.to === '/meine-tickets' && <TicketZaehlerBadge n={ticketDaten.melder?.length ?? 0} />}
            </NavLink>
          ))}

          {(isEinkaufHost || isBlechbedarfHost) && <WerkstoffArchiv />}

          {!isModulHost && <>
          {/* Einkauf läuft als eigenständiges Modul (einkauf.sickinger-portal.de) —
              daher keine Einkauf-Navigation mehr in der Terminliste-Sidebar. */}
          <div className="pt-4 pb-1 px-3 text-xs uppercase text-slate-500 font-semibold">
            Admin
          </div>
          {adminItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg ${
                  isActive ? 'bg-brand text-white' : 'text-slate-300 hover:bg-slate-800'
                }`
              }>
              <span>{item.label}</span>
            </NavLink>
          ))}

          <div className="pt-4 pb-1 px-3 text-xs uppercase text-slate-500 font-semibold">
            Geplant
          </div>
          {phaseItems.map(item => (
            <div key={item.label} className="flex items-center gap-3 px-3 py-2 text-slate-500 text-sm">
              <span>{item.label}</span>
              <span className="ml-auto text-xs">Phase {item.phase}</span>
            </div>
          ))}
          </>}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="text-xs text-slate-400">Version 0.1.0</div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto bg-slate-50">
        <Outlet />
      </main>

      <MotivationOverlay />
      <AutoUpdate />
    </div>
  );
}
